/**
 * BCP document list component.
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  BCPListItem,
  BCPListResponse,
  BCPFilter,
  BCPType,
  BCPStatus,
} from '../types/bcp';
import { BCP_TYPE_LABELS, BCP_STATUS_LABELS } from '../types/bcp';
import { bcpService } from '../services/bcpService';

interface BCPListProps {
  /** Optional tenant ID for filtering */
  tenantId?: string;
  /** Callback when a BCP is selected */
  onSelect?: (bcp: BCPListItem) => void;
  /** Items per page */
  pageSize?: number;
}

export const BCPList: React.FC<BCPListProps> = ({
  tenantId,
  onSelect,
  pageSize = 20,
}) => {
  console.log('[BCPList] Component mounted/rendered');

  const [data, setData] = useState<BCPListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'type' | 'created_at' | 'updated_at' | 'status'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const loadBCPs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('[BCPList] Loading BCPs with params:', {
        tenant_id: tenantId,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        status: 'published',
      });

      const response = await bcpService.listBCPs({
        tenant_id: tenantId,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        // Show both draft and published BCPs
        // status: 'published', // Removed: was hiding draft BCPs
      });

      console.log('[BCPList] Loaded BCPs:', response);
      console.log('[BCPList] Number of items:', response.items.length);
      setData(response);
    } catch (err) {
      console.error('Failed to load BCPs:', err);
      
      let errorMessage = 'Failed to load BCPs';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if ((err as any)?.message) {
        errorMessage = (err as any).message;
      }
      
      if ((err as any)?.code === 'ERR_NETWORK') {
        errorMessage = 'BCPサーバーに接続できません。バックエンドサーバーが起動しているか確認してください。';
      } else if ((err as any)?.code === '404') {
        errorMessage = 'BCP管理APIエンドポイントが見つかりません。';
      } else if ((err as any)?.code === '500') {
        errorMessage = 'サーバー内部エラーが発生しました。';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [tenantId, currentPage, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    loadBCPs();
  }, [loadBCPs]);

  const handleDelete = async (bcpId: string, permanent = false) => {
    console.log('[BCPList] handleDelete called with:', { bcpId, permanent });

    const deleteType = permanent ? '完全削除' : '削除';
    const confirmMessage = permanent
      ? `BCPを完全に削除してもよろしいですか？\n※この操作は取り消せません。`
      : `BCPを削除してもよろしいですか？\nこの操作は取り消せません。`;

    const confirmed = confirm(confirmMessage);
    console.log('[BCPList] User confirmation:', confirmed);

    if (!confirmed) {
      return;
    }

    try {
      console.log('[BCPList] Calling bcpService.deleteBCP with:', { bcpId, tenantId, permanent });
      await bcpService.deleteBCP(bcpId, tenantId, permanent);
      console.log('[BCPList] Delete successful, reloading list');
      await loadBCPs(); // Reload the list
    } catch (err) {
      console.error(`Failed to ${deleteType} BCP:`, err);

      // Extract error message from the error object
      let errorMessage = `BCPの${deleteType}に失敗しました。`;
      if (err && typeof err === 'object') {
        const error = err as any;
        // Check for error message in various possible locations
        if (error.message && error.message.includes('テストデータは削除できません')) {
          errorMessage = 'テストデータは削除できません。';
        } else if (error.details?.detail) {
          errorMessage = error.details.detail;
        } else if (error.message) {
          // Check if it's a permission error
          if (error.message.includes('permission') || error.message.includes('creator')) {
            errorMessage = 'このBCPを削除する権限がありません。作成者のみが削除できます。';
          } else {
            errorMessage = error.message;
          }
        }
      }

      alert(errorMessage);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (loading && !data) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">エラー: {error}</div>
        <button
          onClick={loadBCPs}
          className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
        >
          再試行
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <h2 className="text-xl font-bold text-[#0d357f]">
        作成済みBCP一覧
      </h2>

      {/* Simple Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {data && data.items.length > 0 ? (
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#0d357f]">
                  種別
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#0d357f]">
                  作成日
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#0d357f]">
                  最終更新
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#0d357f]">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {data.items.filter(item => item != null).map((item, index) => {
                console.log('[BCPList] Rendering item:', item.id);
                return (
                <tr key={item.id} className={index > 0 ? 'border-t border-gray-200' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded text-white ${
                      item.type === 'introductory' ? 'bg-[#00a63e]' :
                      item.type === 'simple' ? 'bg-[#0d357f]' :
                      'bg-[#f54900]'
                    }`}>
                      {BCP_TYPE_LABELS[item.type]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4a5565] font-light">
                    {formatDate(item.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4a5565] font-light">
                    {formatDate(item.updated_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-light">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          onSelect?.(item);
                        }}
                        className="px-3 py-1 border border-[#c4c4c4] rounded text-[#0d357f] font-medium hover:bg-gray-50 transition-colors"
                      >
                        開く
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="px-3 py-1 rounded border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12 text-gray-500">
            作成済みのBCPがありません。上記のボタンから新しいBCPを作成してください。
          </div>
        )}
      </div>
    </div>
  );
};
